<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Accordion from 'primevue/accordion'
import AccordionContent from 'primevue/accordioncontent'
import AccordionHeader from 'primevue/accordionheader'
import AccordionPanel from 'primevue/accordionpanel'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Select from 'primevue/select'
import AdvancesCard from '@/components/ui/AdvancesCard.vue'
import DateField from '@/components/ui/DateField.vue'
import { useAuthStore } from '@/stores/auth'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { customerApi, type CustomerSuggestion } from '@/services/customers'
import { usersApi } from '@/services/users'
import {
  STORE_SALE_PRICE_TYPES,
  STORE_SALE_SOURCES,
  storeSalesApi,
  type Advisor,
  type StoreSale,
  type StoreSalePayload,
  type StoreSalePriceType,
} from '@/services/storeSales'
import type { QuoteAdvance } from '@/services/quotes'

/**
 * Formulario de una venta de tienda.
 *
 * Es el mismo para alta y edición, como el cotizador, pero más corto: no hay
 * buscador de productos, sets ni cantidades. Lo que se guarda es una cabecera y
 * un valor único.
 *
 * El acordeón «Datos de la venta» empieza abierto. En el alta no se crea ningún
 * borrador: la venta existe cuando se pulsa «Guardar venta». Los anticipos solo
 * se pueden registrar después, porque antes de guardar no hay venta a la que
 * colgarlos.
 */

const props = defineProps<{
  /** La venta cargada en edición; `null` en el alta. */
  sale: StoreSale | null
}>()

const authStore = useAuthStore()
const { volver: volverAlListado } = useVolverAlListado('store-sales.index')

const isEdit = computed(() => props.sale !== null)
const saving = ref(false)
const generalError = ref<string | null>(null)
const serverErrors = ref<Record<string, string[]>>({})

const customerId = ref<number | null>(null)
const customerName = ref('')
const customerDocument = ref('')
const customerEmail = ref('')
const customerPhone = ref('')
const customerCity = ref('')
const customerSource = ref('')
const attentionLine = ref('')
const priceType = ref<StoreSalePriceType>('retail')
const advisorId = ref<number | null>(null)
const saleDate = ref('')
const amount = ref<number | null>(null)
const isInvoiced = ref(false)
const invoiceNumber = ref('')

const customerSuggestions = ref<CustomerSuggestion[]>([])
const advisors = ref<Advisor[]>([])
const attentionLines = ref<string[]>([])

/** Los anticipos se editan aparte y no viajan en el guardado de la venta. */
const advances = ref<QuoteAdvance[]>([])
const advancesTotal = ref('0')
const balance = ref('0')

const expandedTabs = ref<string[]>(['datos'])

/** Fecha de hoy en `YYYY-MM-DD`, sin pasar por UTC. */
function hoy(): string {
  const ahora = new Date()
  const mes = String(ahora.getMonth() + 1).padStart(2, '0')
  const dia = String(ahora.getDate()).padStart(2, '0')

  return `${ahora.getFullYear()}-${mes}-${dia}`
}

const attentionLineOptions = computed(() => {
  const lineas = [...attentionLines.value, attentionLine.value]
    .map((linea) => linea.trim())
    .filter((linea) => linea !== '')

  return [
    { label: 'Sin línea', value: '' },
    ...[...new Set(lineas)]
      .sort((a, b) => a.localeCompare(b, 'es'))
      .map((linea) => ({ label: linea, value: linea })),
  ]
})

function rellenar(venta: StoreSale | null): void {
  if (venta === null) {
    customerId.value = null
    customerName.value = ''
    customerDocument.value = ''
    customerEmail.value = ''
    customerPhone.value = ''
    customerCity.value = ''
    customerSource.value = ''
    attentionLine.value = authStore.user?.attention_line ?? ''
    priceType.value = 'retail'
    advisorId.value = authStore.user?.id ?? null
    saleDate.value = hoy()
    amount.value = null
    isInvoiced.value = false
    invoiceNumber.value = ''
    advances.value = []
    advancesTotal.value = '0'
    balance.value = '0'
    return
  }

  customerId.value = venta.customer_id
  customerName.value = venta.customer_name
  customerDocument.value = venta.document ?? ''
  customerEmail.value = venta.customer_email ?? ''
  customerPhone.value = venta.customer_phone ?? ''
  customerCity.value = venta.customer_city ?? ''
  customerSource.value = venta.customer_source ?? ''
  attentionLine.value = venta.attention_line ?? ''
  priceType.value = venta.price_type
  advisorId.value = venta.user_id
  saleDate.value = venta.sale_date
  amount.value = Number.parseFloat(venta.amount)
  isInvoiced.value = venta.is_invoiced
  invoiceNumber.value = venta.invoice_number ?? ''
  advances.value = venta.advances
  advancesTotal.value = venta.advances_total
  balance.value = venta.balance
}

watch(() => props.sale, rellenar, { immediate: true })

async function loadAdvisors(): Promise<void> {
  try {
    const { data } = await storeSalesApi.advisors()
    advisors.value = Array.isArray(data.data) ? data.data : []
  } catch {
    advisors.value = []
  }
}

async function loadAttentionLines(): Promise<void> {
  try {
    const { data } = await usersApi.getAttentionLines()
    attentionLines.value = Array.isArray(data.data) ? data.data : []
  } catch {
    attentionLines.value = []
  }
}

async function searchCustomers(): Promise<void> {
  const term = customerName.value.trim()

  if (term.length < 2) {
    customerSuggestions.value = []
    return
  }

  try {
    const { data } = await customerApi.search(term)
    customerSuggestions.value = data.data
  } catch {
    customerSuggestions.value = []
  }
}

/** El nombre escrito a mano deja de estar enlazado al cliente anterior. */
function onCustomerNameInput(): void {
  customerId.value = null
  void searchCustomers()
}

function pickCustomer(cliente: CustomerSuggestion): void {
  customerId.value = cliente.id
  customerName.value = cliente.name
  customerDocument.value = cliente.document ?? ''
  customerEmail.value = cliente.email ?? ''
  customerPhone.value = cliente.phone ?? ''
  customerCity.value = cliente.city ?? ''
  customerSource.value = cliente.source ?? ''
  customerSuggestions.value = []
}

function nullable(texto: string): string | null {
  const limpio = texto.trim()

  return limpio === '' ? null : limpio
}

function payload(): StoreSalePayload {
  return {
    customer_id: customerId.value,
    customer_name: customerName.value.trim(),
    document: nullable(customerDocument.value),
    customer_email: nullable(customerEmail.value),
    customer_phone: nullable(customerPhone.value),
    customer_city: nullable(customerCity.value),
    customer_source: nullable(customerSource.value),
    attention_line: nullable(attentionLine.value),
    sale_date: saleDate.value === '' ? null : saleDate.value,
    price_type: priceType.value,
    user_id: advisorId.value,
    amount: Number(amount.value ?? 0),
    is_invoiced: isInvoiced.value,
    invoice_number: nullable(invoiceNumber.value),
  }
}

function extractServerErrors(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as {
    validationErrors?: Record<string, string[]>
    response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } }
  }

  return maybeAxios.validationErrors ?? maybeAxios.response?.data?.error?.details?.fields ?? {}
}

async function guardar(): Promise<void> {
  serverErrors.value = {}
  generalError.value = null

  if (customerName.value.trim() === '') {
    serverErrors.value = { customer_name: ['La venta necesita un cliente.'] }
    return
  }

  if (amount.value === null || amount.value <= 0) {
    serverErrors.value = { amount: ['Escribe un valor de venta mayor que cero.'] }
    return
  }

  if (isInvoiced.value && invoiceNumber.value.trim() === '') {
    serverErrors.value = { invoice_number: ['Escribe el número de factura para marcarla como facturada.'] }
    return
  }

  saving.value = true

  try {
    if (props.sale === null) {
      await storeSalesApi.create(payload())
    } else {
      await storeSalesApi.update(props.sale.id, payload())
    }

    // Alta y edición vuelven al listado con los filtros con los que se llegó.
    volverAlListado()
  } catch (cause: unknown) {
    serverErrors.value = extractServerErrors(cause)
    generalError.value = Object.keys(serverErrors.value).length > 0
      ? null
      : 'No se pudo guardar la venta. Inténtalo de nuevo.'
  } finally {
    saving.value = false
  }
}

function cambiaAnticipos(totals: { advances_total: string; balance: string; advances: QuoteAdvance[] }): void {
  advancesTotal.value = totals.advances_total
  balance.value = totals.balance
  advances.value = totals.advances
}

onMounted(async () => {
  await Promise.all([loadAdvisors(), loadAttentionLines()])
})
</script>

<template>
  <form
    class="space-y-4"
    @submit.prevent="guardar"
  >
    <Message
      v-if="generalError !== null"
      severity="error"
      :closable="false"
      role="alert"
    >
      {{ generalError }}
    </Message>

    <Accordion
      v-model:value="expandedTabs"
      :multiple="true"
    >
      <AccordionPanel value="datos">
        <AccordionHeader>
          Datos de la venta
        </AccordionHeader>
        <AccordionContent>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="sm:col-span-2 lg:col-span-1">
              <label
                for="store-sale-customer"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Cliente
              </label>
              <InputText
                id="store-sale-customer"
                v-model="customerName"
                placeholder="Nombre o documento"
                class="w-full"
                :invalid="!!serverErrors.customer_name"
                data-testid="store-sale-customer"
                @input="onCustomerNameInput"
              />
              <p class="mt-1 text-xs text-surface-500 dark:text-surface-400">
                Escribe el nombre o el número de documento. Da igual con puntos o sin ellos.
              </p>

              <p
                v-if="serverErrors.customer_name"
                class="mt-1 text-xs text-red-600 dark:text-red-400"
              >
                {{ serverErrors.customer_name[0] }}
              </p>

              <ul
                v-if="customerSuggestions.length > 0"
                class="mt-2 divide-y divide-surface-200 rounded border border-surface-200 dark:divide-surface-700 dark:border-surface-700"
              >
                <li
                  v-for="cliente in customerSuggestions"
                  :key="cliente.id"
                >
                  <button
                    type="button"
                    class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-surface-100 dark:hover:bg-surface-800"
                    @click="pickCustomer(cliente)"
                  >
                    <span>{{ cliente.name }}</span>
                    <span
                      v-if="cliente.document"
                      class="font-mono text-xs text-surface-500"
                    >
                      {{ cliente.document }}
                    </span>
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <label
                for="store-sale-document"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Documento
              </label>
              <InputText
                id="store-sale-document"
                v-model="customerDocument"
                class="w-full"
                :invalid="!!serverErrors.document"
              />
            </div>

            <div>
              <label
                for="store-sale-email"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Correo
              </label>
              <InputText
                id="store-sale-email"
                v-model="customerEmail"
                type="email"
                class="w-full"
                :invalid="!!serverErrors.customer_email"
              />
            </div>

            <div>
              <label
                for="store-sale-phone"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Teléfono
              </label>
              <InputText
                id="store-sale-phone"
                v-model="customerPhone"
                class="w-full"
                :invalid="!!serverErrors.customer_phone"
              />
            </div>

            <div>
              <label
                for="store-sale-city"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Ciudad
              </label>
              <InputText
                id="store-sale-city"
                v-model="customerCity"
                class="w-full"
                :invalid="!!serverErrors.customer_city"
              />
            </div>

            <div>
              <label
                for="store-sale-source"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Origen
              </label>
              <Select
                id="store-sale-source"
                v-model="customerSource"
                :options="STORE_SALE_SOURCES"
                option-label="label"
                option-value="value"
                placeholder="Sin origen"
                class="w-full"
                :invalid="!!serverErrors.customer_source"
              />
            </div>

            <div>
              <label
                for="store-sale-advisor"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Asesor
              </label>
              <Select
                id="store-sale-advisor"
                v-model="advisorId"
                :options="advisors"
                option-label="name"
                option-value="id"
                placeholder="Elige un asesor"
                class="w-full"
                :invalid="!!serverErrors.user_id"
              />
            </div>

            <div>
              <label
                for="store-sale-price-type"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Tipo de facturación
              </label>
              <Select
                id="store-sale-price-type"
                v-model="priceType"
                :options="STORE_SALE_PRICE_TYPES"
                option-label="label"
                option-value="value"
                class="w-full"
                :invalid="!!serverErrors.price_type"
              />
            </div>

            <div>
              <DateField
                v-model="saleDate"
                input-id="store-sale-date"
                label="Fecha de venta"
              />
            </div>

            <div class="sm:col-span-2">
              <label
                for="store-sale-attention-line"
                class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Línea de atención
              </label>
              <InputText
                v-if="!isEdit"
                id="store-sale-attention-line"
                v-model="attentionLine"
                disabled
                class="w-full"
              />
              <Select
                v-else
                id="store-sale-attention-line"
                v-model="attentionLine"
                :options="attentionLineOptions"
                option-label="label"
                option-value="value"
                class="w-full"
                :invalid="!!serverErrors.attention_line"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>

    <div class="rounded-xl border border-surface-200 bg-white p-4 dark:border-surface-700 dark:bg-surface-900">
      <h2 class="text-base font-semibold text-surface-900 dark:text-surface-100">
        Valor y facturación
      </h2>

      <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label
            for="store-sale-amount"
            class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Valor de la venta
          </label>
          <InputNumber
            v-model="amount"
            input-id="store-sale-amount"
            mode="currency"
            currency="COP"
            locale="es-CO"
            :min="0"
            :max-fraction-digits="0"
            class="w-full"
            :invalid="!!serverErrors.amount"
            data-testid="store-sale-amount"
          />
          <p
            v-if="serverErrors.amount"
            class="mt-1 text-xs text-red-600 dark:text-red-400"
          >
            {{ serverErrors.amount[0] }}
          </p>
        </div>

        <div>
          <label
            for="store-sale-invoice"
            class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Número de factura
          </label>
          <InputText
            id="store-sale-invoice"
            v-model="invoiceNumber"
            class="w-full"
            :invalid="!!serverErrors.invoice_number"
            data-testid="store-sale-invoice-number"
          />
          <p
            v-if="serverErrors.invoice_number"
            class="mt-1 text-xs text-red-600 dark:text-red-400"
          >
            {{ serverErrors.invoice_number[0] }}
          </p>
        </div>

        <div class="flex items-start gap-3 pt-6">
          <Checkbox
            v-model="isInvoiced"
            input-id="store-sale-is-invoiced"
            binary
            data-testid="store-sale-is-invoiced"
          />
          <div>
            <label
              for="store-sale-is-invoiced"
              class="text-sm font-medium text-surface-700 dark:text-surface-300"
            >
              Facturada
            </label>
            <p class="text-xs text-surface-500 dark:text-surface-400">
              Una venta con número de factura se considera facturada, aunque el indicador esté apagado.
            </p>
          </div>
        </div>
      </div>
    </div>

    <AdvancesCard
      v-if="isEdit && sale !== null"
      :quote-id="sale.id"
      kind="store"
      :advances="advances"
      :advances-total="advancesTotal"
      :balance="balance"
      @cambia="cambiaAnticipos"
    />

    <div class="flex flex-wrap justify-end gap-2">
      <Button
        type="button"
        label="Cancelar"
        severity="secondary"
        outlined
        @click="volverAlListado()"
      />
      <Button
        type="submit"
        :label="isEdit ? 'Guardar cambios' : 'Guardar venta'"
        icon="pi pi-save"
        :loading="saving"
        data-testid="store-sale-save"
      />
    </div>
  </form>
</template>
